import { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import meService from '../../services/me-service'
import mock_clasament from './mock_clasament.json'
import grileService from '../../services/grile-service'
import Tooltip from '@mui/material/Tooltip'
import GrileService from '../../services/grile-service'

export const SortBy = Object.freeze({
  SOLVED_GRIDS: 0,
  CORRECTLY_SOLVED_GRIDS: 1,
  CORRECT_PERCENTAGE: 2,
})

const useRanking = () => {
  const maxLeaderboardAnimationDelay = 3 // seconds
  const nrUsersDisplayed = 15
  const pages = [1, 2, 3, 4, 5]
  const MAX_PAGE = 5
  const MIN_PAGE = 1

  const [sortType, setSortType] = useState(SortBy.SOLVED_GRIDS)
  const [usersData, setUsersData] = useState(null)
  const [currentUserData, setCurrentUserData] = useState({})
  const [currentUserPosition, setCurrentUserPosition] = useState([
    -1, -1, -1, -1,
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [hasSubscription, setHasSubscription] = useState(undefined)
  const [courseValue, setCourseValue] = useState('all')
  const [filterPeriodValue, setFilterPeriodValue] = useState('all')
  const [faculty, setFaculty] = useState('all')
  const [faculties, setFaculties] = useState([])
  const [currentPage, setCurrentPage] = useState(1)

  const handlePageChange = useCallback(page => {
    setCurrentPage(page)
  }, [])

  const handleNextPage = useCallback(() => {
    if (currentPage === MAX_PAGE) return
    setCurrentPage(prev => prev + 1)
  }, [currentPage])

  const handlePreviousPage = useCallback(() => {
    if (currentPage === MIN_PAGE) return
    setCurrentPage(prev => prev - 1)
  }, [currentPage])

  const handleSelectedCourse = useCallback(event => {
    setCourseValue(event.target.value)
  }, [])

  const handlePeriodValue = useCallback(event => {
    setFilterPeriodValue(event.target.value)
  }, [])

  const handleFacultyValue = useCallback(event => {
    setFaculty(event.target.value)
  }, [])

  const handleSetPublicName = useCallback(
    async isNamePublic => {
      const payload = { value: isNamePublic }
      const response = await meService.setPublicName(payload)

      // update name and public_name fields in the currentUser state
      const copyUser = { ...currentUserData }
      copyUser.public_name = response?.public_name
      copyUser.name = response?.name
      setCurrentUserData(copyUser)

      // updating current user fields in the leaderboard structure if the user is present.
      let userInTopFlag = false
      const copyUsersData = [...usersData]

      for (let index = 0; index < copyUsersData.length; index++) {
        const currentUserFromArray = copyUsersData[index].find(
          user => user.id === copyUser.id,
        )
        const currentUserIndexInArray =
          copyUsersData[index].indexOf(currentUserFromArray)

        if (currentUserIndexInArray !== -1) {
          userInTopFlag = true
          copyUsersData[index][currentUserIndexInArray].public_name =
            response?.public_name
          copyUsersData[index][currentUserIndexInArray].name = response?.name
        }
      }

      if (userInTopFlag) {
        setUsersData(copyUsersData)
      }
    },
    [usersData, currentUserData],
  )

  const handleGetDisplayName = useCallback(user => {
    return (
      <Tooltip title={user.public_name ? user.name : ''}>
        <span className="tooltiptext-leaderboard">
          {user.public_name ? user.name : user.nickname}
        </span>
      </Tooltip>
    )
  }, [])

  const handleGetDisplayEye = useCallback(
    user => {
      return (
        <Tooltip
          title={
            user.public_name
              ? 'Ascunde celorlalți utilizatori numele tău'
              : 'Afisează numele tău celorlalți utilizatori'
          }
        >
          <FontAwesomeIcon
            onClick={() => handleSetPublicName(!user.public_name)}
            className="ml-1 cursor-pointer"
            icon={user.public_name ? faEyeSlash : faEye}
          />
        </Tooltip>
      )
    },
    [handleSetPublicName],
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        let response

        if (process.env.REACT_APP_TESTING) {
          response = mock_clasament
        } else {
          response = await grileService.getClasament(
            faculty,
            filterPeriodValue,
            courseValue,
          )
        }

        if (!response) {
          throw new Error('No valid response received')
        }

        const sortedUsersData = [
          response.grile_rezolvate ?? 0,
          response.grile_rezolvate_corect,
          response.procent_corecte,
        ]

        if (sortedUsersData) {
          setUsersData(sortedUsersData)

          const user = response.current_user

          if (!user.details) {
            user.details = {}
          }

          if (
            user.details.grile_rezolvate === 0 ||
            user.details.grile_rezolvate === '0' ||
            isNaN(user.details.grile_rezolvate)
          ) {
            user.details.grile_rezolvate = 0
            user.details.grile_rezolvate_corect = 0
            user.details.indicii = 0
          } else {
            user.details.grile_rezolvate = parseInt(
              user.details.grile_rezolvate ?? 0,
            )
            user.details.grile_rezolvate_corect = parseInt(
              user.details.grile_rezolvate_corect,
            )
            user.details.indicii = parseInt(user.details.indicii)
          }

          setCurrentUserData(user.details)

          if (user.locuri) {
            setHasSubscription(true)

            if (user.locuri.loc_grile_rezolvate !== '0') {
              setCurrentUserPosition([
                parseInt(user.locuri.loc_grile_rezolvate) - 1,
                parseInt(user.locuri.loc_grile_rezolvate_corect) - 1,
                parseInt(user.locuri.loc_procent_corecte) - 1,
                parseInt(user.locuri.loc_procent_indicii) - 1,
              ])
            } else {
              setCurrentUserPosition(Array(4).fill(nrUsersDisplayed + 1))
            }
          }
        }
      } catch (error) {
        console.error('Error fetching ranking data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [faculty, filterPeriodValue, courseValue])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GrileService.getAllFaculties()
        setFaculties(response)
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  return {
    maxLeaderboardAnimationDelay,
    sortType,
    setSortType,
    currentUserPosition,
    hasSubscription,
    handleSetPublicName,
    handleGetDisplayEye,
    handleGetDisplayName,
    usersData,
    currentUserData,
    nrUsersDisplayed,
    isLoading,
    handleSelectedCourse,
    handlePeriodValue,
    courseValue,
    filterPeriodValue,
    faculties,
    handleFacultyValue,
    faculty,
    handlePageChange,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    pages,
    MAX_PAGE,
    MIN_PAGE,
  }
}

export default useRanking
